/* eslint-disable @typescript-eslint/no-explicit-any */
import { Card } from '@mui/material'
import Container from '@mui/system/Container'
import { Formik } from 'formik'
import { omit } from 'lodash'
import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import userFormContent from './contents/IndividualUserFormContent'
import Form from './elements/Form'
import FormButton from './elements/FormButton'
import FormButtonContainer from './elements/FormButtonContainer'
import FormGrid from './elements/FormGrid'
import userSchema from './schemas/IndividualUserSchema'
import { EVENT_SHIRT_DETAILS, SHIRT_KEY } from '../../constants/models'
import { EventShirtDetailsProps } from '../../constants/types'
import useEventDetails from '../../hooks/useEventDetails'
import Button from '../elements/Button'
import EmptyState from '../EmptyState'

interface Props {
  user?: any
  updateData: (values) => any
  onCancel?: React.MouseEventHandler<HTMLButtonElement>
  showRepeater: boolean
  hasBackSteps: boolean
  editing?: boolean
  setLoading?: (value: boolean) => void
  displayError?: (values: any) => void
  hasShirt2?: boolean
  shirtDetails?: EventShirtDetailsProps
}

const RegistrationForm: React.FC<Props> = ({
  user: initUser,
  updateData,
  onCancel,
  showRepeater,
  hasBackSteps,
  editing,
  setLoading,
  displayError,
}) => {
  const { slug } = useParams()
  const {
    event,
    apiLoading: loading,
    apiError,
  } = useEventDetails({
    slug,
  })
  // const user = initUser

  const [fullShirtDetails, setFullShirtDetails] = useState<any>({
    hasShirt2: initUser.hasShirt2 || false,
    shirtDetails: initUser.shirtDetails || EVENT_SHIRT_DETAILS,
  })

  useEffect(() => {
    setLoading(loading)
  }, [loading, setLoading])

  useEffect(() => {
    if (initUser) {
      setFullShirtDetails((prevState) => ({
        ...prevState,
        hasShirt2: initUser?.hasShirt2 || false,
        shirtDetails: initUser?.shirtDetails,
      }))
    }
  }, [initUser])

  const renderContent = () => {
    if (apiError)
      return (
        <EmptyState header={apiError.title} text={apiError.message}>
          <Link to="/events">
            <Button variant="contained" color="secondary">
              BACK TO EVENTS
            </Button>
          </Link>
        </EmptyState>
      )

    if (!event)
      return (
        <EmptyState
          header="Hmm… We cannot find the event you are looking for"
          text=""
        />
      )
    return (
      <>
        <Formik
          initialValues={{
            ...initUser,
            ...(SHIRT_KEY.SHIRT1 in fullShirtDetails.shirtDetails &&
              'TYPE_OPTIONS' in fullShirtDetails.shirtDetails.SHIRT1 && {
                shirtType: initUser.shirtType2 ?? null,
              }),
            ...(fullShirtDetails.hasShirt2 && {
              shirtSize2: initUser.shirtSize2 ?? null,
              ...('TYPE_OPTIONS' in fullShirtDetails.shirtDetails.SHIRT2 && {
                shirtType2: initUser.shirtType2 ?? null,
              }),
            }),
          }}
          validateOnMount={editing ? true : false}
          enableReinitialize
          validateOnBlur={false}
          validationSchema={userSchema}
          onSubmit={async (values, actions) => {
            setLoading(true)
            Object.keys(values).forEach((key) => {
              if (typeof values[key] === 'string')
                values[key] = values[key]?.trim()
            })
            const newValues = {
              ...omit(values, ['shirtSize2', 'shirtType2']),
              ...(values.hasShirt2 && {
                ...('shirtSize2' in values && {
                  shirtSize2: values.shirtSize2,
                }),
                ...('shirtType2' in values && {
                  shirtType2: values.shirtType2,
                }),
              }),
            }
            const [, error] = await updateData(newValues)
            if (error) {
              displayError({ title: error.title, message: error.message })
            }
            setLoading(false)
            actions.setSubmitting(false)
          }}>
          {(form) => (
            <Form id="user-form">
              <>
                <Card>
                  <FormGrid
                    content={userFormContent({
                      isVirtual: event?.isVirtual,
                      showRepeater,
                      formDisabled: form.isSubmitting,
                      hasBibName: event?.hasBibName,
                      hasJersey: initUser.hasJersey,
                      fullShirtDetails,
                    })}
                  />
                </Card>
              </>
              <FormButtonContainer wrap={false}>
                <FormButton
                  secondary
                  outlined
                  onClick={onCancel}
                  disabled={form.isSubmitting}>
                  {hasBackSteps ? 'Back' : 'Cancel'}
                </FormButton>
                <FormButton
                  submit
                  primary
                  outlined
                  disabled={!form.isValid || form.isSubmitting}>
                  {'Next'}
                </FormButton>
              </FormButtonContainer>
            </Form>
          )}
        </Formik>
      </>
    )
  }

  return <Container disableGutters>{!loading && renderContent()}</Container>
}
export default RegistrationForm
